import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import DemoRequestDropdown from '../../components/ui/DemoRequestDropdown';
import useExchangeRate from '../../hooks/useExchangeRate';

const ORDER_STATUS_OPTIONS = [
  { value: 'pending', label: 'pending' },
  { value: 'processing', label: 'processing' },
  { value: 'shipped', label: 'shipped' },
  { value: 'delivered', label: 'delivered' },
  { value: 'cancelled', label: 'cancelled' },
];
const PAYMENT_STATUS_OPTIONS = [
  { value: 'pending', label: 'pending', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'paid', label: 'paid', color: 'bg-green-100 text-green-700' },
  { value: 'failed', label: 'failed', color: 'bg-red-100 text-red-700' },
];

const PAYMENT_METHOD_OPTIONS = [
  { value: 'pay_on_delivery', label: 'Pay on Delivery' },
  { value: 'online', label: 'Online' },
];

const CURRENCY_OPTIONS = [
  { value: '€', label: '€' },
  { value: 'TND', label: 'TND' },
];

// Helper to get color classes for status
const getStatusColor = (type, value) => {
  if (type === 'payment') {
    if (value === 'paid') return 'bg-green-100 text-green-700';
    if (value === 'failed') return 'bg-red-100 text-red-700';
    return 'bg-yellow-100 text-yellow-700'; // pending
  }
  if (type === 'order') {
    if (value === 'delivered' || value === 'shipped') return 'bg-green-100 text-green-700';
    if (value === 'cancelled') return 'bg-red-100 text-red-700';
    if (value === 'processing') return 'bg-yellow-100 text-yellow-700';
    return 'bg-yellow-50 text-yellow-700'; // pending
  }
  return '';
};

const getPaymentStatusColor = (option) => {
  if (!option) return '';
  if (option.value === 'paid') return 'bg-green-100 text-green-700';
  if (option.value === 'failed') return 'bg-red-100 text-red-700';
  return 'bg-yellow-100 text-yellow-700'; // pending
};
const getOrderStatusColor = (option) => {
  if (!option) return '';
  if (option.value === 'delivered') return 'bg-green-100 text-green-700';
  if (option.value === 'shipped') return 'bg-blue-100 text-blue-700';
  if (option.value === 'processing') return 'bg-orange-100 text-orange-700';
  if (option.value === 'cancelled') return 'bg-red-100 text-red-700';
  return 'bg-yellow-100 text-yellow-700'; // pending
};

const Orders = () => {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [editStatus, setEditStatus] = useState({}); // { [orderId]: { orderStatus, paymentStatus } }
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [products, setProducts] = useState([]); // <-- fetched products
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState('');
  const [newOrder, setNewOrder] = useState({
    items: [{ productId: '', variantIndex: 0, quantity: 1 }],
    customerInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      country: '',
      postalCode: '',
    },
    total: 0,
    currency: 'EUR',
    paymentMethod: 'pay_on_delivery',
    paymentStatus: 'pending',
    orderStatus: 'pending',
    orderId: '',
  });
  const tndExchangeRate = useExchangeRate();

  const fetchOrders = async () => {
    setLoading(true);
    setError('');
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
      const res = await fetch(`${apiBaseUrl}/api/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch orders');
      const data = await res.json();
      setOrders(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line
  }, [token]);

  // Fetch products when modal opens
  useEffect(() => {
    if (showCreateModal) {
      setProductsLoading(true);
      setProductsError('');
      fetch(`${import.meta.env.VITE_API_BASE_URL}/api/admin/products`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(res => res.json())
        .then(data => setProducts(data))
        .catch(() => setProductsError('Failed to fetch products'))
        .finally(() => setProductsLoading(false));
    }
  }, [showCreateModal, token]);

  const handleEditChange = (orderId, field, e) => {
    // e.target.value for native, e.target.value for DemoRequestDropdown
    const value = e.target ? e.target.value : e;
    setEditStatus(prev => ({
      ...prev,
      [orderId]: {
        ...prev[orderId],
        [field]: value
      }
    }));
  };

  const handleUpdateStatus = async (orderId) => {
    const { orderStatus, paymentStatus } = editStatus[orderId] || {};
    setUpdatingId(orderId);
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
      const res = await fetch(`${apiBaseUrl}/api/admin/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ orderStatus, paymentStatus })
      });
      if (!res.ok) throw new Error('Failed to update order status');
      await fetchOrders();
      setEditStatus(prev => ({ ...prev, [orderId]: {} }));
    } catch (err) {
      alert(err.message || 'Failed to update order status');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!confirm('Are you sure you want to delete this order? This action cannot be undone.')) {
      return;
    }
    
    setDeletingId(orderId);
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
      const res = await fetch(`${apiBaseUrl}/api/admin/orders/${orderId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete order');
      await fetchOrders();
    } catch (err) {
      alert(err.message || 'Failed to delete order');
    } finally {
      setDeletingId(null);
    }
  };

  const handleCreateOrder = async () => {
    setCreatingOrder(true);
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
      // Build items array with name, weight, price, quantity, productId
      const items = newOrder.items.map(item => {
        const product = products.find(p => p._id === item.productId);
        const variant = product?.variants?.[item.variantIndex] || {};
        return {
          productId: item.productId,
          name: product?.name || '',
          weight: variant.weight || '',
          price: parseFloat(variant.price) || 0,
          quantity: parseInt(item.quantity) || 1,
        };
      });
      const orderToSend = {
        ...newOrder,
        items,
        total: calculateTotal(),
      };
      const res = await fetch(`${apiBaseUrl}/api/admin/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(orderToSend)
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to create order');
      }
      
      await fetchOrders();
      setShowCreateModal(false);
      setNewOrder({
        items: [{ productId: '', variantIndex: 0, quantity: 1 }],
        customerInfo: {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          address: '',
          city: '',
          country: '',
          postalCode: '',
        },
        total: 0,
        currency: 'EUR',
        paymentMethod: 'pay_on_delivery',
        paymentStatus: 'pending',
        orderStatus: 'pending',
        orderId: '',
      });
    } catch (err) {
      alert(err.message || 'Failed to create order');
    } finally {
      setCreatingOrder(false);
    }
  };

  const updateNewOrder = (field, value) => {
    setNewOrder(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateCustomerInfo = (field, value) => {
    setNewOrder(prev => ({
      ...prev,
      customerInfo: {
        ...prev.customerInfo,
        [field]: value
      }
    }));
  };

  const updateItem = (index, field, value) => {
    setNewOrder(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index ? { ...item, [field]: value, ...(field === 'productId' ? { variantIndex: 0 } : {}) } : item
      )
    }));
  };

  const addItem = () => {
    setNewOrder(prev => ({
      ...prev,
      items: [...prev.items, { productId: '', variantIndex: 0, quantity: 1 }]
    }));
  };

  const removeItem = (index) => {
    if (newOrder.items.length > 1) {
      setNewOrder(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    }
  };

  const calculateTotal = () => {
    return newOrder.items.reduce((sum, item) => {
      const product = products.find(p => p._id === item.productId);
      const variant = product?.variants?.[item.variantIndex];
      let price = parseFloat(variant?.price) || 0;
      if (newOrder.currency === 'TND') price = price * tndExchangeRate;
      const quantity = parseInt(item.quantity) || 0;
      return sum + (price * quantity);
    }, 0);
  };

  return (
    <div className="p-2 md:p-8">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl md:text-2xl font-bold">Orders</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium"
        >
          Create New Order
        </button>
      </div>

      {loading ? (
        <div>Loading orders...</div>
      ) : error ? (
        <div className="text-red-500">{error}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full bg-white rounded-xl shadow-lg text-xs sm:text-sm md:text-sm">
            <thead className="bg-blue-100">
              <tr>
                <th className="px-2 md:px-4 py-2 border">Order ID</th>
                <th className="px-2 md:px-4 py-2 border min-w-[16rem]">Products</th>
                <th className="px-2 md:px-4 py-2 border">Name</th>
                <th className="px-2 md:px-4 py-2 border">Email</th>
                <th className="px-2 md:px-4 py-2 border">Phone</th>
                <th className="px-2 md:px-4 py-2 border min-w-[12rem]">Address</th>
                <th className="px-2 md:px-4 py-2 border">City</th>
                <th className="px-2 md:px-4 py-2 border">Country</th>
                <th className="px-2 md:px-4 py-2 border">Total</th>
                <th className="px-2 md:px-4 py-2 border">Currency</th>
                <th className="px-2 md:px-4 py-2 border">Payment Method</th>
                <th className="px-2 md:px-4 py-2 border">Payment Status</th>
                <th className="px-2 md:px-4 py-2 border">Order Status</th>
                <th className="px-2 md:px-4 py-2 border">Date</th>
                <th className="px-2 md:px-4 py-2 border">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(order => (
                <tr key={order._id}>
                  <td className="px-2 md:px-4 py-2 border">{order._id}</td>
                  <td className="px-2 md:px-4 py-2 border">
                    {order.products && order.products.length > 0 ? (
                      <ul className="list-disc pl-4">
                        {order.products.map((item, idx) => (
                          <li key={idx}>
                            {item.name} ({item.weight}) x {item.quantity}
                          </li>
                        ))}
                      </ul>
                    ) : 'N/A'}
                  </td>
                  <td className="px-2 md:px-4 py-2 border">{order.customerName || ''}</td>
                  <td className="px-2 md:px-4 py-2 border">{order.email || ''}</td>
                  <td className="px-2 md:px-4 py-2 border">{order.phone || ''}</td>
                  <td className="px-2 md:px-4 py-2 border">{order.address || ''}</td>
                  <td className="px-2 md:px-4 py-2 border">{order.city || ''}</td>
                  <td className="px-2 md:px-4 py-2 border">{order.country || ''}</td>
                  <td className="px-2 md:px-4 py-2 border">{order.total?.toFixed(2) ?? '0.00'}</td>
                  <td className="px-2 md:px-4 py-2 border">{order.currency || 'N/A'}</td>
                  <td className="px-2 md:px-4 py-2 border">{order.paymentMethod || 'N/A'}</td>
                  <td className="px-2 md:px-4 py-2 border">
                    <DemoRequestDropdown
                      name="paymentStatus"
                      options={PAYMENT_STATUS_OPTIONS}
                      value={editStatus[order._id]?.paymentStatus || order.paymentStatus || 'pending'}
                      onChange={e => handleEditChange(order._id, 'paymentStatus', e)}
                      dropdownClassName="min-w-[120px]"
                      getOptionClassName={getPaymentStatusColor}
                    />
                  </td>
                  <td className="px-2 md:px-4 py-2 border">
                    <DemoRequestDropdown
                      name="orderStatus"
                      options={ORDER_STATUS_OPTIONS}
                      value={editStatus[order._id]?.orderStatus || order.orderStatus || 'pending'}
                      onChange={e => handleEditChange(order._id, 'orderStatus', e)}
                      dropdownClassName="min-w-[120px]"
                      getOptionClassName={getOrderStatusColor}
                    />
                  </td>
                  <td className="px-2 md:px-4 py-2 border">{order.createdAt ? new Date(order.createdAt).toLocaleDateString() : ''}</td>
                  <td className="px-2 md:px-4 py-2 border">
                    <div className="flex gap-1">
                      <button
                        className="bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded text-xs disabled:opacity-50"
                        disabled={updatingId === order._id}
                        onClick={() => handleUpdateStatus(order._id)}
                      >
                        {updatingId === order._id ? 'Updating...' : 'Update'}
                      </button>
                      <button
                        className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded text-xs disabled:opacity-50"
                        disabled={deletingId === order._id}
                        onClick={() => handleDeleteOrder(order._id)}
                      >
                        {deletingId === order._id ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Order Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold">Create New Order</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Customer Information */}
                <div>
                  <h4 className="font-semibold mb-3">Customer Information</h4>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        placeholder="First Name"
                        value={newOrder.customerInfo.firstName}
                        onChange={(e) => updateCustomerInfo('firstName', e.target.value)}
                        className="border rounded px-3 py-2 text-sm"
                      />
                      <input
                        type="text"
                        placeholder="Last Name"
                        value={newOrder.customerInfo.lastName}
                        onChange={(e) => updateCustomerInfo('lastName', e.target.value)}
                        className="border rounded px-3 py-2 text-sm"
                      />
                    </div>
                    <input
                      type="email"
                      placeholder="Email *"
                      value={newOrder.customerInfo.email}
                      onChange={(e) => updateCustomerInfo('email', e.target.value)}
                      className="border rounded px-3 py-2 text-sm w-full"
                      required
                    />
                    <input
                      type="tel"
                      placeholder="Phone"
                      value={newOrder.customerInfo.phone}
                      onChange={(e) => updateCustomerInfo('phone', e.target.value)}
                      className="border rounded px-3 py-2 text-sm w-full"
                    />
                    <input
                      type="text"
                      placeholder="Address"
                      value={newOrder.customerInfo.address}
                      onChange={(e) => updateCustomerInfo('address', e.target.value)}
                      className="border rounded px-3 py-2 text-sm w-full"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        placeholder="City"
                        value={newOrder.customerInfo.city}
                        onChange={(e) => updateCustomerInfo('city', e.target.value)}
                        className="border rounded px-3 py-2 text-sm"
                      />
                      <input
                        type="text"
                        placeholder="Country"
                        value={newOrder.customerInfo.country}
                        onChange={(e) => updateCustomerInfo('country', e.target.value)}
                        className="border rounded px-3 py-2 text-sm"
                      />
                    </div>
                    <input
                      type="text"
                      placeholder="Postal Code"
                      value={newOrder.customerInfo.postalCode}
                      onChange={(e) => updateCustomerInfo('postalCode', e.target.value)}
                      className="border rounded px-3 py-2 text-sm w-full"
                    />
                  </div>
                </div>

                {/* Order Details */}
                <div>
                  <h4 className="font-semibold mb-3">Order Details</h4>
                  <div className="space-y-3">
                    <input
                      type="text"
                      placeholder="Order ID (optional)"
                      value={newOrder.orderId}
                      onChange={(e) => updateNewOrder('orderId', e.target.value)}
                      className="border rounded px-3 py-2 text-sm w-full"
                    />
                    <DemoRequestDropdown
                      name="currency"
                      options={CURRENCY_OPTIONS}
                      value={newOrder.currency}
                      onChange={e => updateNewOrder('currency', e.target.value)}
                      dropdownClassName="w-full"
                    />
                    <DemoRequestDropdown
                      name="paymentMethod"
                      options={PAYMENT_METHOD_OPTIONS}
                      value={newOrder.paymentMethod}
                      onChange={e => updateNewOrder('paymentMethod', e.target.value)}
                      dropdownClassName="w-full"
                    />
                    <DemoRequestDropdown
                      name="paymentStatus"
                      options={PAYMENT_STATUS_OPTIONS}
                      value={newOrder.paymentStatus}
                      onChange={e => updateNewOrder('paymentStatus', e.target.value)}
                      dropdownClassName="w-full"
                      getOptionClassName={getPaymentStatusColor}
                    />
                    <DemoRequestDropdown
                      name="orderStatus"
                      options={ORDER_STATUS_OPTIONS}
                      value={newOrder.orderStatus}
                      onChange={e => updateNewOrder('orderStatus', e.target.value)}
                      dropdownClassName="w-full"
                      getOptionClassName={getOrderStatusColor}
                    />
                    <div className="text-lg font-semibold">
                      Total: {calculateTotal().toFixed(2)} {newOrder.currency}
                    </div>
                  </div>
                </div>
              </div>

              {/* Items */}
              <div className="mt-6">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold">Items</h4>
                  <button
                    onClick={addItem}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm"
                  >
                    Add Item
                  </button>
                </div>
                {productsLoading ? (
                  <div>Loading products...</div>
                ) : productsError ? (
                  <div className="text-red-500">{productsError}</div>
                ) : (
                  <div className="space-y-3">
                    {newOrder.items.map((item, index) => {
                      const product = products.find(p => p._id === item.productId);
                      const variant = product?.variants?.[item.variantIndex] || {};
                      let displayPrice = parseFloat(variant.price) || 0;
                      if (newOrder.currency === 'TND') displayPrice = displayPrice * tndExchangeRate;
                      return (
                        <div key={index} className="grid grid-cols-12 gap-2 items-center">
                          {/* IMAGE COLUMN - far left */}
                          <div className="col-span-1 flex items-center justify-center">
                            {product && product.image && (
                              <img
                                src={product.image}
                                alt={product.name}
                                className="w-12 h-12 object-cover rounded"
                              />
                            )}
                          </div>
                          {/* PRODUCT SELECT */}
                          <div className="col-span-3 flex items-center gap-2">
                            <select
                              className="border rounded px-3 py-2 text-sm w-full"
                              value={item.productId}
                              onChange={e => updateItem(index, 'productId', e.target.value)}
                              required
                            >
                              <option value="">Select Product</option>
                              {products.map(p => (
                                <option key={p._id} value={p._id}>{p.name}</option>
                              ))}
                            </select>
                          </div>
                          {/* WEIGHT SELECT */}
                          <div className="col-span-2">
                            <select
                              className="border rounded px-3 py-2 text-sm w-full"
                              value={item.variantIndex}
                              onChange={e => updateItem(index, 'variantIndex', Number(e.target.value))}
                              disabled={!product}
                              required
                            >
                              {product?.variants?.map((v, idx) => (
                                <option key={idx} value={idx}>{v.weight}</option>
                              ))}
                            </select>
                          </div>
                          {/* PRICE (auto, currency aware) */}
                          <div className="col-span-2">
                            <input
                              type="number"
                              value={displayPrice ? displayPrice.toFixed(2) : ''}
                              className="border rounded px-3 py-2 text-sm w-full bg-gray-100"
                              readOnly
                              placeholder="Price"
                            />
                          </div>
                          {/* QUANTITY */}
                          <div className="col-span-2">
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={e => updateItem(index, 'quantity', e.target.value)}
                              className="border rounded px-3 py-2 text-sm w-full"
                              required
                            />
                          </div>
                          {/* REMOVE BUTTON */}
                          <div className="col-span-2 flex gap-1">
                            <button
                              onClick={() => removeItem(index)}
                              disabled={newOrder.items.length === 1}
                              className="bg-red-500 hover:bg-red-600 text-white px-2 py-2 rounded text-sm disabled:opacity-50"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateOrder}
                  disabled={creatingOrder || !newOrder.customerInfo.email || newOrder.items.some(item => !item.productId || !item.quantity)}
                  className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded disabled:opacity-50"
                >
                  {creatingOrder ? 'Creating...' : 'Create Order'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders; 